/**
 * Route guards (T2.11).
 *
 * The requirement is stronger than "hide the button": a viewer must not reach an
 * editing control by ANY route, including a pasted URL. So every guarded screen
 * is gated here, at the route, rather than by a component deciding not to draw
 * something. Two consequences worth stating:
 *
 *   - A control a role may not use is ABSENT, not disabled. A disabled control
 *     still tells the user the feature exists and still ships its markup.
 *   - The browser guard is a courtesy, never the enforcement. The API checks
 *     every request through `requireBlueprintAccess` (design.md § 5), so a user
 *     who defeats this one gains a screen and no data.
 *
 * Nothing here decides a role of its own. The global role comes from /auth/me
 * and the per-blueprint role comes from the blueprint payload; both are the
 * API's answer, not a guess made in the browser.
 */
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '../lib/auth';
import { useTerms } from '../lib/terms';

/**
 * Shown while /auth/me is in flight. It must render nothing role-dependent:
 * guessing "signed in" for a moment is how a viewer sees an editing control
 * flash up before it is taken away.
 */
export function AuthPending() {
  return (
    <div className="empty" role="status" aria-live="polite">
      Checking your session…
    </div>
  );
}

export function NotPermitted({ detail }: { detail: string }) {
  return (
    <div className="empty" role="alert">
      <b>You do not have access to this.</b>
      <br />
      {detail}
    </div>
  );
}

/**
 * Any signed-in user. Sends an anonymous visitor to login, remembering where.
 *
 * A user whose password was set by an administrator (`mustChangePassword`) may
 * reach the Account screen and nothing else: the API answers every other request
 * with 403 until the password is changed (T4.4), so letting them onto a screen
 * that can only fail would look like a broken application. `allowPasswordChange`
 * is passed by the Account route alone.
 */
export function RequireAuth({
  children,
  allowPasswordChange = false,
}: {
  children: ReactNode;
  allowPasswordChange?: boolean;
}) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <AuthPending />;
  if (!isAuthenticated) {
    // `state.from` lets the login screen return the user to the URL they typed,
    // so a pasted deep link survives a sign-in.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (user?.mustChangePassword && !allowPasswordChange) {
    return <Navigate to="/account" replace />;
  }
  return <>{children}</>;
}

/**
 * Global admin only — user, client and terminology administration.
 * A signed-in non-admin gets a refusal, not a redirect: bouncing them somewhere
 * else reads as a broken link rather than a permission boundary.
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) return <AuthPending />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.mustChangePassword) return <Navigate to="/account" replace />;
  if (user.globalRole !== 'admin') {
    return <NotPermitted detail="This screen is for administrators." />;
  }
  return <>{children}</>;
}

/** Keeps a signed-in user off the login screen rather than showing a dead form. */
export function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <AuthPending />;
  if (isAuthenticated) return <Navigate to="/blueprints" replace />;
  return <>{children}</>;
}

/**
 * The per-blueprint equivalent, for a subtree that may only be shown to an
 * editor or admin. Phase 2 renders read-only, so nothing uses it yet for a
 * control — it gates the version selector's draft entries, and Phase 3's
 * editing routes hang off it.
 */
export function RequireBlueprintEditor({
  role,
  children,
}: {
  role: 'admin' | 'editor' | 'viewer' | undefined;
  children: ReactNode;
}) {
  const terms = useTerms();
  if (role === undefined) return <AuthPending />;
  if (role === 'viewer') {
    return (
      <NotPermitted
        detail={`You can view this ${terms.noun('blueprint')} but not change it.`}
      />
    );
  }
  return <>{children}</>;
}
